import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  TrendingUp,
  Users,
  MessageSquare,
  Target,
  Copy,
  Music
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TikTokAds() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({
    enabled: false,
    advertiser_id: '',
    access_token: '',
    auto_whatsapp: true,
    welcome_message: '¡Hola {nombre}! Vimos tu interés en TikTok. ¿Te gustaría agendar una cita?'
  });

  const webhookUrl = `${API_URL}/api/webhook/tiktok/leads`;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, statsRes, leadsRes] = await Promise.all([
        fetch(`${API_URL}/api/clinic/${clinicId}/tiktok-lead-settings`, { headers }),
        fetch(`${API_URL}/api/clinic/${clinicId}/tiktok-stats`, { headers }),
        fetch(`${API_URL}/api/clinic/${clinicId}/tiktok-leads?limit=10`, { headers })
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (leadsRes.ok) {
        setLeads(await leadsRes.json());
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/tiktok-lead-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: '¡Configuración guardada!' });
      } else {
        setMessage({ type: 'error', text: 'Error al guardar' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    }
    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setMessage({ type: 'success', text: 'URL copiada al portapapeles' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Music className="w-7 h-7 text-pink-500" />
            TikTok Ads
          </h1>
          <p className="text-gray-500 mt-1">
            Recibe leads automáticamente de tus anuncios en TikTok
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.total_leads}</p>
                    <p className="text-xs text-gray-500">Total Leads</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.whatsapp_sent}</p>
                    <p className="text-xs text-gray-500">WhatsApp Enviados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.conversion_rate}%</p>
                    <p className="text-xs text-gray-500">Conversión</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.this_week}</p>
                    <p className="text-xs text-gray-500">Esta Semana</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Webhook URL */}
        <Card className="bg-pink-500/10 border-pink-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              URL del Webhook
            </CardTitle>
            <CardDescription>
              Configura esta URL en TikTok for Business para recibir leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={webhookUrl}
                readOnly
                className="bg-slate-800 border-slate-600 font-mono text-sm"
              />
              <Button onClick={copyWebhook} variant="outline">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-4 p-4 bg-slate-800/50 rounded-lg text-sm text-gray-400">
              <p className="font-medium text-white mb-2">Pasos para configurar:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Ve a <a href="https://business-api.tiktok.com/portal" target="_blank" rel="noopener noreferrer" className="text-pink-400 underline">TikTok for Business Portal</a></li>
                <li>Crea una App o selecciona una existente</li>
                <li>Activa "Lead Generation" en Solutions</li>
                <li>Ve a Webhooks → Agrega tu callback URL</li>
                <li>Suscribe el evento "lead.generated"</li>
                <li>Verifica tu dominio si es necesario</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Configuración</CardTitle>
                <CardDescription>Personaliza cómo recibir y procesar leads de TikTok</CardDescription>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Advertiser ID */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">ID de Anunciante (Advertiser ID)</label>
              <Input
                value={settings.advertiser_id || ''}
                onChange={(e) => setSettings({ ...settings, advertiser_id: e.target.value })}
                placeholder="7123456789012345678"
                className="bg-slate-700/50 border-slate-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Encuéntralo en TikTok Ads Manager → Configuración de cuenta
              </p>
            </div>

            {/* Access Token */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Access Token (opcional)</label>
              <Input
                type="password"
                value={settings.access_token || ''}
                onChange={(e) => setSettings({ ...settings, access_token: e.target.value })}
                placeholder="Token de acceso..."
                className="bg-slate-700/50 border-slate-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Solo necesario si quieres obtener datos adicionales del lead
              </p>
            </div>

            {/* Auto WhatsApp */}
            <div>
              <label className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg cursor-pointer">
                <Switch
                  checked={settings.auto_whatsapp}
                  onCheckedChange={(checked) => setSettings({ ...settings, auto_whatsapp: checked })}
                />
                <div>
                  <p className="font-medium">Enviar WhatsApp automático</p>
                  <p className="text-xs text-gray-500">Envía mensaje de bienvenida al recibir un lead</p>
                </div>
              </label>
            </div>

            {/* Welcome Message */}
            {settings.auto_whatsapp && (
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Mensaje de bienvenida</label>
                <textarea
                  value={settings.welcome_message}
                  onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-white resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variables: {'{nombre}'} - Nombre del lead
                </p>
              </div>
            )}

            <Button
              onClick={saveSettings}
              disabled={saving}
              className="w-full bg-gradient-to-r from-pink-600 to-purple-600"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Music className="w-4 h-4 mr-2" />}
              Guardar Configuración
            </Button>
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg">Últimos Leads de TikTok</CardTitle>
            <CardDescription>Leads recibidos de tus campañas de TikTok</CardDescription>
          </CardHeader>
          <CardContent>
            {leads.length > 0 ? (
              <div className="space-y-3">
                {leads.map((lead, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        lead.whatsapp_sent ? 'bg-green-500/20' : 'bg-pink-500/20'
                      }`}>
                        <span className="text-lg font-bold">
                          {lead.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{lead.name || 'Sin nombre'}</p>
                        <p className="text-xs text-gray-500">{lead.phone || lead.email || 'Sin contacto'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString('es-MX', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {lead.whatsapp_sent && (
                        <span className="text-xs text-green-400 flex items-center gap-1 justify-end">
                          <MessageSquare className="w-3 h-3" />
                          WhatsApp enviado
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No hay leads de TikTok aún. Configura el webhook para empezar a recibir leads.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
